// Which league the page shows. The league is fixed for a page load and comes
// from the URL: /ahl is the AHL, everything else the NHL. Tab routes stay in
// the hash (/ahl#kausi). Switching league navigates to the other URL.

export const LEAGUES = ['NHL', 'AHL'] as const;
export type League = (typeof LEAGUES)[number];

// What the league's data has. AHL data is a lighter set (see the backend README).
export interface LeagueFeatures {
    // Points per game day (/api/points), shown as the graphs on Kausi › Maapisteet.
    pointsHistory: boolean;
    // ESPN injury news (Kausi › Uutiset).
    news: boolean;
    // The previous game day on Tänään (tapping the date).
    previousDay: boolean;
    // Kausi › Maajoukkue.
    nationalTeam: boolean;
    // Liiga › Pudotuspelit and Liiga › Tankkaus.
    playoffs: boolean;
    tankRace: boolean;
    // Division and wild card lines in the standings.
    playoffLines: boolean;
    // Standings are ranked by points percentage.
    pointsPct: boolean;
    // Skater time on ice is recorded (goalie time on ice always is).
    skaterToi: boolean;
    // Stats table columns (column ids) the league's data does not have.
    hiddenSkaterColumns: readonly string[];
    hiddenGoalieColumns: readonly string[];
    // The division letters, for the standings footnote.
    divisionNote: string;
}

export const LEAGUE_FEATURES: Record<League, LeagueFeatures> = {
    NHL: {
        pointsHistory: true,
        news: true,
        previousDay: true,
        nationalTeam: true,
        playoffs: true,
        tankRace: true,
        playoffLines: true,
        pointsPct: false,
        skaterToi: true,
        hiddenSkaterColumns: [],
        hiddenGoalieColumns: [],
        divisionNote: 'M/A/C/P',
    },
    AHL: {
        pointsHistory: false,
        news: false,
        previousDay: false,
        nationalTeam: false,
        playoffs: false,
        tankRace: false,
        playoffLines: false,
        pointsPct: true,
        skaterToi: false,
        hiddenSkaterColumns: ['toi', 'shotPct', 'faceoffPct', 'contract', 'capHit'],
        hiddenGoalieColumns: ['contract', 'capHit'],
        divisionNote: 'A/N/C/P',
    },
};

const AHL_PATH = /^\/ahl\/?$/i;

export const leagueFromPath = (pathname: string): League => (AHL_PATH.test(pathname) ? 'AHL' : 'NHL');

export const currentLeague = (): League => leagueFromPath(window.location.pathname);

// The page of the league, keeping the tab route.
export const leagueUrl = (league: League, hash: string): string => (league === 'AHL' ? '/ahl' : '/') + hash;

// A fresh visit to the front page goes to the AHL when the AHL was the last
// league chosen in the menu. A reload or back/forward never redirects, so an
// NHL tab reloaded for a new app version stays NHL.
export const shouldRedirectToAhl = (pathname: string, remembered: League, navigationType: string | undefined): boolean =>
    pathname === '/' && remembered === 'AHL' && navigationType === 'navigate';

export function navigationType(): string | undefined {
    try {
        const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
        return entry?.type;
    } catch {
        return undefined;
    }
}

// Opens another page (kept separate so tests can replace it).
export function navigate(url: string): void {
    window.location.assign(url);
}
